import { useCallback, useState } from 'react';
// Hooks
import { useAuth } from '@/hooks/useAuth';
// Utils
import { brand } from '@/brand/brand';
import {
  getAllUserVotes,
  getCharterById,
} from '@/resources/chain-ledger-service.resource';

const errState = {
  SERVER: 'server',
  NOT_FOUND: 'unfound',
};

export function useCharter() {
  const { memberData, fetcher } = useAuth();

  const [charter, setCharter] = useState({});
  const [hasVoted, setHasVoted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [votingEnded, setVotingEnded] = useState(false);
  const [error, setError] = useState('');

  const userId = memberData?.userId;
  // This code returns a single charter by charterKey, if you need to search an array of charters, please use getCharters api call instead.
  const getCharterInfo = useCallback(() => {
    const charterRequest = getCharterById({
      charterKey: brand.pages.voting?.charterKey,
    });
    if (!charterRequest) {
      setVotingEnded(true);
      setIsLoading(false);
      setError('');
      return;
    }

    fetcher(charterRequest)
      .then((data) => {
        const timeNow = new Date();
        const periodEnded = data.closeDate < timeNow.toISOString();
        setCharter(data);
        if (!periodEnded) {
          getHasVoted(userId, data);
        } else {
          setVotingEnded(true);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        const raw = err?.response?.data;
        const message = [
          typeof raw?.detail === 'string' ? raw.detail : '',
          typeof raw?.title === 'string' ? raw.title : '',
        ].join(' ');
        if (
          status === 404 ||
          message.includes('could not be found') ||
          message.includes('not found')
        ) {
          setError(errState.NOT_FOUND);
        } else {
          setError(errState.SERVER);
        }
        setIsLoading(false);
      });
  }, [fetcher, userId]);

  // if the user has voted, do not show charter
  const getHasVoted = useCallback(
    async (userId, charter) => {
      fetcher(getAllUserVotes({ userId }))
        .then((data) => {
          const votes = Array.isArray(data) ? data : [];
          const hasVoted = votes.find((vote) => {
            return vote.charterId === charter.documentId;
          });
          setHasVoted(!!hasVoted);
          setIsLoading(false);
        })
        .catch(() => {
          setError(errState.SERVER);
        });
    },
    [fetcher]
  );

  return {
    charter,
    error,
    errState,
    hasVoted,
    isLoading,
    getCharterInfo,
    getHasVoted,
    setHasVoted,
    votingEnded,
  };
}
